// sim/projects.ts — REGION-SCALE COLONY PROJECTS. The long-horizon objective layer:
// a colony commits to ONE project at a time and pours surplus FOOD + worker LABOUR
// into it over many ticks (workers + resources + time) until it COMPLETES. A finished
// project's PERMANENT bonus (a food dividend) then pays out every cadence forever, and
// the completion is worth one-time leaderboard value + an evolution-energy reward.
//
// DESIGN DISCIPLINE
//   - Deterministic: pure integer SoA, no RNG, fixed colony/project order, exact FNV
//     hash. Allocation-free per tick.
//   - Survival-safe: the build only draws food ABOVE a safety reserve, so a project
//     can never starve its colony to death (the lesson from earlier autonomy work).
//   - Self-contained: reads/writes only the ColonySystem (food + caste census), so it
//     rides in the ecosystem world without touching the colony layer's own hash. The
//     PLAYER starts projects (a recorded action, replay-safe). By default rivals leave
//     theirs Available, so their behaviour is byte-identical to a project-less world;
//     an OPT-IN policy (config.rivalAutoStart) can let established, idle rivals commit
//     on their own — deterministically, so it still replays exactly.
//   - Typed effects: every project pays the universal per-cadence food dividend, AND
//     carries a distinct bonus KIND (BONUS_TYPE.*) that consuming systems can read off
//     the accessors (bonusTypeOf / effectOf / hasEffect) and apply when wired.

import { CASTE } from '../types.ts';
import type { ColonyId, System, Tick } from '../types.ts';
import { fnvInit, fnvU32 } from '../hash.ts';
import type { ColonySystem } from './colony.ts';
import { PROJECT_COUNT, PROJECT_STATUS, PROJECT_DEFS, BONUS_TYPE } from './projectsData.ts';
import type { Project, BonusType } from './projectsData.ts';

/**
 * Gene-drop-rate percent ADDED per completed GeneDropRate-type project (the "cultivated
 * mutagens" bonus). One such project ⇒ +50% drop rate (a 150% scale); the effect stacks
 * if more GeneDropRate projects exist. Read by `geneDropPctOf`; a consuming death-drop
 * roll multiplies the result into its drop scale.
 */
export const GENE_DROP_PCT_PER_PROJECT = 50;

/**
 * War-strength DEFENSE bonus ADDED per completed Defense-type project (the "deep
 * sheltered galleries" bonus) when the colony is DEFENDING its territory. ~2× the flat
 * defenderBonus (12), so a fortified colony is meaningfully harder to dislodge. Read by
 * `defenseBonusOf`; applied in `rivals.resolveWar`.
 */
export const DEFENSE_STRENGTH_PER_PROJECT = 25;

/**
 * Extra carrying-capacity ceiling ADDED per completed PopCap-type project ("rafts +
 * shoreline runs feed more mouths") — ON TOP of the universal per-project capacity
 * weight, so a PopCap project is the population-ceiling specialist. Read by
 * `popCapBonusOf`; applied in `CarryingCapacitySystem` via `attachPopCapBonus`.
 */
export const POP_CAP_PER_PROJECT = 16;

/**
 * Whether (and on what terms) a NON-PLAYER colony may auto-commit to a project without
 * an explicit `start` call. Default is OFF everywhere; when off the system is byte-for-
 * byte identical to a project-less world (only recorded player `start`s take effect).
 */
export interface RivalAutoStartConfig {
  /** Master switch. When false/absent, rivals never auto-start (today's behaviour). */
  readonly autoStartForRivals: boolean;
  /**
   * "ESTABLISHED" floor #1: a rival needs at least this population on top of a project's
   * own `minPopulation` gate before it will commit (so it doesn't start the instant it
   * can). Default 0 — the per-project `minPopulation` is the only population bar.
   */
  readonly autoStartMinPopulation: number;
  /**
   * "ESTABLISHED" floor #2: a rival won't commit unless it holds at least this much food
   * (so a broke colony doesn't lock into a build it can't feed). Default = safetyReserve.
   */
  readonly autoStartFoodFloor: number;
  /**
   * Optional bespoke predicate that REPLACES the built-in established/idle test. Pure +
   * deterministic only (it is called inside the hashed update). Returns true iff `cid`
   * should look for a project to auto-start this cadence. When absent, the built-in
   * established/idle rule is used.
   */
  readonly autoStart?: ((cid: ColonyId) => boolean) | undefined;
}

export interface ProjectsConfig {
  /** Ticks between investment passes (projects advance on a cadence, not every tick). */
  readonly cadence: number;
  /** Labour credited per worker-caste body per cadence (workers do the building). */
  readonly laborPerWorker: number;
  /** Max food drawn from the colony per cadence while building (paces the resource cost). */
  readonly foodPerCadence: number;
  /** Never spend a colony's food below this reserve — the build can't cause famine. */
  readonly safetyReserve: number;
  /**
   * OPT-IN rival autonomy. Absent ⇒ rivals never auto-start (byte-identical default).
   * When present with `autoStartForRivals: true`, non-player colonies begin a project on
   * their own once established + idle (see RivalAutoStartConfig).
   */
  readonly rivalAutoStart?: RivalAutoStartConfig | undefined;
}

export function defaultProjectsConfig(): ProjectsConfig {
  return { cadence: 20, laborPerWorker: 2, foodPerCadence: 6, safetyReserve: 24 };
}

export class ColonyProjectSystem implements System {
  readonly name = 'projects';
  readonly capacity: number;
  private readonly colonies: ColonySystem;
  private readonly config: ProjectsConfig;

  // Per (colony × project) SoA, index [cid * PROJECT_COUNT + project].
  private readonly status: Uint8Array; // PROJECT_STATUS.* (Available = 0 default)
  private readonly labor: Int32Array;  // worker-progress accrued toward laborNeeded
  private readonly food: Int32Array;   // food invested so far toward foodCost

  /** Total projects completed across all colonies (deterministic; folded into the hash). */
  completedTotal = 0;

  /**
   * OPTIONAL region-unlock gate (late-bound via `attachRegionGate`). When wired, `start`
   * ALSO requires the project's region to be unlocked — so a headless/CLI start respects
   * the same region requirement the web `startProject` pre-check enforces. Absent ⇒ the
   * legacy population-only gate (byte-identical); the gate draws no RNG and reads only the
   * caller's deterministic region state.
   */
  private regionUnlockedOf: ((region: number) => boolean) | null = null;

  constructor(colonies: ColonySystem, config: ProjectsConfig) {
    this.colonies = colonies;
    this.capacity = colonies.capacity;
    this.config = config;
    this.status = new Uint8Array(this.capacity * PROJECT_COUNT);
    this.labor = new Int32Array(this.capacity * PROJECT_COUNT);
    this.food = new Int32Array(this.capacity * PROJECT_COUNT);
  }

  /**
   * Begin `project` for colony `cid`. Gated: the project must be Available, NO other
   * project may be Active (one long-term goal at a time), and the colony must field
   * the project's `minPopulation`. Pure deterministic state, so a replayed start
   * reproduces exactly. Returns true iff it started.
   */
  start(cid: ColonyId, project: Project): boolean {
    if (cid < 0 || cid >= this.capacity) return false;
    const def = PROJECT_DEFS[project];
    if (def === undefined) return false;
    const base = cid * PROJECT_COUNT;
    if ((this.status[base + project] as number) !== PROJECT_STATUS.Available) return false;
    if (this.activeProjectOf(cid) >= 0) return false;
    if (this.colonies.populationAt(cid) < def.minPopulation) return false;
    // REGION GATE (opt-in): a project can only begin once its themed region is reached.
    // Unwired ⇒ skipped (population-only, byte-identical), so the sim CAN enforce the
    // requirement headlessly without forcing it on worlds that don't track region unlocks.
    if (this.regionUnlockedOf !== null && !this.regionUnlockedOf(def.region)) return false;
    this.status[base + project] = PROJECT_STATUS.Active;
    return true;
  }

  /**
   * Late-bind the region-unlock gate (mirrors the other optional seams). Once wired,
   * `start` requires the project's region to be unlocked; not wiring it leaves the
   * population-only gate, byte-identical.
   */
  attachRegionGate(source: (region: number) => boolean): void {
    this.regionUnlockedOf = source;
  }

  update(tick: Tick): void {
    if (tick % this.config.cadence !== 0) return;
    const count = this.colonies.count;
    for (let cid = 0; cid < count; cid++) {
      const base = cid * PROJECT_COUNT;
      for (let p = 0; p < PROJECT_COUNT; p++) {
        const st = this.status[base + p] as number;
        if (st === PROJECT_STATUS.Active) {
          this.advance(cid, base, p);
        } else if (st === PROJECT_STATUS.Complete) {
          // PERMANENT BONUS: the finished works keep producing, cadence after cadence.
          const dv = (PROJECT_DEFS[p] as { foodDividend: number }).foodDividend;
          if (dv > 0) this.colonies.depositFood(cid, dv);
        }
      }
    }
    // OPT-IN: let idle, established rivals commit to a project of their own. Runs AFTER
    // the advance/dividend pass (so a freshly-started project first builds NEXT cadence),
    // and is a single guarded branch — zero work + zero allocation when off (the default).
    const ras = this.config.rivalAutoStart;
    if (ras !== undefined && ras.autoStartForRivals) this.autoStartRivals(ras, tick);
  }

  /**
   * Deterministic auto-start pass for non-player colonies. Fixed colony iteration order
   * (0..count) and a hashed project choice, so the same seed + same world state always
   * picks the same project for the same colony at the same tick. Allocation-free.
   */
  private autoStartRivals(ras: RivalAutoStartConfig, tick: Tick): void {
    const count = this.colonies.count;
    for (let cid = 0; cid < count; cid++) {
      // PLAYER colonies are queen-protected; rivals are everyone else. Skip the player
      // (its projects are recorded actions) and any extinct colony.
      if (this.colonies.isProtected(cid)) continue;
      if (!this.colonies.isAlive(cid)) continue;
      // IDLE: not already building. (One long-term goal at a time, same as `start`.)
      if (this.activeProjectOf(cid) >= 0) continue;
      // ESTABLISHED: either the bespoke predicate, or the built-in pop+food floors.
      if (!this.rivalIsReady(ras, cid)) continue;
      const choice = this.chooseRivalProject(ras, cid, tick);
      if (choice >= 0) this.start(cid, choice as Project);
    }
  }

  /** Established/idle test for a rival: the optional predicate, else the built-in floors. */
  private rivalIsReady(ras: RivalAutoStartConfig, cid: ColonyId): boolean {
    if (ras.autoStart !== undefined) return ras.autoStart(cid);
    return (
      this.colonies.populationAt(cid) >= ras.autoStartMinPopulation &&
      this.colonies.foodAt(cid) >= ras.autoStartFoodFloor
    );
  }

  /**
   * Pick an Available project this rival can actually begin (its `minPopulation` met),
   * chosen deterministically. We hash the colony's identity + census + tick into a stable
   * offset, then scan the project roster from there and take the first startable one — so
   * the choice varies across colonies/ticks/seeds yet replays identically. Returns -1 if
   * the colony qualifies for nothing (e.g. all complete, or under every project's gate).
   */
  private chooseRivalProject(ras: RivalAutoStartConfig, cid: ColonyId, tick: Tick): number {
    const base = cid * PROJECT_COUNT;
    const pop = this.colonies.populationAt(cid);
    let h = fnvInit();
    h = fnvU32(h, cid);
    h = fnvU32(h, tick);
    h = fnvU32(h, pop);
    h = fnvU32(h, this.colonies.foodAt(cid));
    h = fnvU32(h, ras.autoStartMinPopulation);
    const offset = h % PROJECT_COUNT;
    for (let i = 0; i < PROJECT_COUNT; i++) {
      const p = (offset + i) % PROJECT_COUNT;
      if ((this.status[base + p] as number) !== PROJECT_STATUS.Available) continue;
      if (pop < (PROJECT_DEFS[p] as { minPopulation: number }).minPopulation) continue;
      return p;
    }
    return -1;
  }

  /** One investment pass for an Active project: accrue labour, draw surplus food, finish. */
  private advance(cid: ColonyId, base: number, p: number): void {
    const cfg = this.config;
    const def = PROJECT_DEFS[p] as (typeof PROJECT_DEFS)[number];
    const li = base + p;

    // LABOUR — workers (and the Worker-like Specialist reserve) build it.
    const workers =
      this.colonies.casteCountAt(cid, CASTE.Worker) + this.colonies.casteCountAt(cid, CASTE.Specialist);
    const labor = this.labor[li] as number;
    if (workers > 0 && labor < def.laborNeeded) {
      const next = labor + workers * cfg.laborPerWorker;
      this.labor[li] = next > def.laborNeeded ? def.laborNeeded : next;
    }

    // RESOURCES — draw food only from the surplus above the safety reserve.
    const paid = this.food[li] as number;
    if (paid < def.foodCost) {
      const surplus = this.colonies.foodAt(cid) - cfg.safetyReserve;
      if (surplus > 0) {
        let want = def.foodCost - paid;
        if (want > cfg.foodPerCadence) want = cfg.foodPerCadence;
        if (want > surplus) want = surplus;
        if (want > 0 && this.colonies.spendFood(cid, want)) this.food[li] = paid + want;
      }
    }

    // COMPLETE when both labour and food are fully paid.
    if ((this.labor[li] as number) >= def.laborNeeded && (this.food[li] as number) >= def.foodCost) {
      this.status[li] = PROJECT_STATUS.Complete;
      this.completedTotal++;
    }
  }

  // --- Accessors (web / hash / tests) ----------------------------------------

  statusOf(cid: ColonyId, project: Project): number {
    return this.status[cid * PROJECT_COUNT + project] as number;
  }
  laborOf(cid: ColonyId, project: Project): number {
    return this.labor[cid * PROJECT_COUNT + project] as number;
  }
  foodOf(cid: ColonyId, project: Project): number {
    return this.food[cid * PROJECT_COUNT + project] as number;
  }
  isComplete(cid: ColonyId, project: Project): boolean {
    return (this.status[cid * PROJECT_COUNT + project] as number) === PROJECT_STATUS.Complete;
  }

  // --- Bonus-type seam --------------------------------------------------------
  // Each project also carries a distinct, themed effect KIND (BONUS_TYPE.*). These
  // accessors expose it so consuming systems (defence, pop-cap, gene-drop rate…) can
  // read what a project does WITHOUT this module depending on them. Reading a type
  // applies nothing on its own — the universal food-dividend payout is unchanged; a
  // consumer wires each kind when it's ready. Allocation-free, pure reads.

  /** The static bonus KIND a project confers (from its definition). */
  bonusTypeOf(project: Project): BonusType {
    return (PROJECT_DEFS[project] as { bonusType: BonusType }).bonusType;
  }

  /**
   * True iff colony `cid` has COMPLETED at least one project of the given bonus kind —
   * the question a consuming system asks ("does this colony have the Defense effect?").
   */
  hasEffect(cid: ColonyId, type: BonusType): boolean {
    const base = cid * PROJECT_COUNT;
    for (let p = 0; p < PROJECT_COUNT; p++) {
      if ((this.status[base + p] as number) !== PROJECT_STATUS.Complete) continue;
      if ((PROJECT_DEFS[p] as { bonusType: BonusType }).bonusType === type) return true;
    }
    return false;
  }

  /**
   * How many of colony `cid`'s COMPLETED projects confer `type` — lets a consumer scale
   * an effect by how many sources back it (e.g. stacked pop-cap). 0 if none.
   */
  effectCountOf(cid: ColonyId, type: BonusType): number {
    const base = cid * PROJECT_COUNT;
    let n = 0;
    for (let p = 0; p < PROJECT_COUNT; p++) {
      if ((this.status[base + p] as number) !== PROJECT_STATUS.Complete) continue;
      if ((PROJECT_DEFS[p] as { bonusType: BonusType }).bonusType === type) n++;
    }
    return n;
  }

  /**
   * Per-colony gene-drop-rate percent from completed GeneDropRate-type projects — the
   * "cultivated mutagens" effect. 100 when the colony has none; each such completed
   * project adds GENE_DROP_PCT_PER_PROJECT. A consuming death-drop roll (castes.ts)
   * multiplies this into its drop scale, so a colony that built a mutagen project loots
   * genes faster. Pure integer read; 100 ⇒ neutral (byte-identical for that colony).
   */
  geneDropPctOf(cid: ColonyId): number {
    return 100 + GENE_DROP_PCT_PER_PROJECT * this.effectCountOf(cid, BONUS_TYPE.GeneDropRate);
  }

  /**
   * Per-colony war-strength DEFENSE bonus from completed Defense-type projects — the
   * "deep sheltered galleries" effect. 0 with none; each such completed project adds
   * DEFENSE_STRENGTH_PER_PROJECT. Read by `rivals.resolveWar` when this colony DEFENDS,
   * so a fortified colony holds its territory better. Pure integer read; 0 ⇒ neutral.
   */
  defenseBonusOf(cid: ColonyId): number {
    return DEFENSE_STRENGTH_PER_PROJECT * this.effectCountOf(cid, BONUS_TYPE.Defense);
  }

  /**
   * Per-colony EXTRA carrying-capacity bonus from completed PopCap-type projects — the
   * "rafts + shoreline runs feed more mouths" effect. 0 with none; each such completed
   * project adds POP_CAP_PER_PROJECT. Read by `CarryingCapacitySystem` (via
   * `attachPopCapBonus`) on top of the universal project weight. Pure integer read.
   */
  popCapBonusOf(cid: ColonyId): number {
    return POP_CAP_PER_PROJECT * this.effectCountOf(cid, BONUS_TYPE.PopCap);
  }

  /**
   * The bonus KIND of the colony's MOST RECENTLY meaningful completed project — its
   * highest-id completed project, since the roster is an ascending ladder (later = the
   * bigger commitment). Returns -1 when the colony has finished nothing. A convenience
   * for consumers/UI that want a single headline effect per colony.
   */
  effectOf(cid: ColonyId): BonusType | -1 {
    const base = cid * PROJECT_COUNT;
    for (let p = PROJECT_COUNT - 1; p >= 0; p--) {
      if ((this.status[base + p] as number) === PROJECT_STATUS.Complete) {
        return (PROJECT_DEFS[p] as { bonusType: BonusType }).bonusType;
      }
    }
    return -1;
  }

  /** The colony's currently-Active project, or -1 if none is in progress. */
  activeProjectOf(cid: ColonyId): number {
    const base = cid * PROJECT_COUNT;
    for (let p = 0; p < PROJECT_COUNT; p++) {
      if ((this.status[base + p] as number) === PROJECT_STATUS.Active) return p;
    }
    return -1;
  }

  /** How many projects this colony has finished. */
  completedCountOf(cid: ColonyId): number {
    const base = cid * PROJECT_COUNT;
    let n = 0;
    for (let p = 0; p < PROJECT_COUNT; p++) {
      if ((this.status[base + p] as number) === PROJECT_STATUS.Complete) n++;
    }
    return n;
  }

  /** Sum of completed projects' leaderboard value (the colony's project standing). */
  leaderboardValueOf(cid: ColonyId): number {
    const base = cid * PROJECT_COUNT;
    let v = 0;
    for (let p = 0; p < PROJECT_COUNT; p++) {
      if ((this.status[base + p] as number) === PROJECT_STATUS.Complete) {
        v += (PROJECT_DEFS[p] as { leaderboardValue: number }).leaderboardValue;
      }
    }
    return v;
  }

  /** Sum of completed projects' Mutation-Energy reward (the evolution opportunity). */
  energyRewardOf(cid: ColonyId): number {
    const base = cid * PROJECT_COUNT;
    let e = 0;
    for (let p = 0; p < PROJECT_COUNT; p++) {
      if ((this.status[base + p] as number) === PROJECT_STATUS.Complete) {
        e += (PROJECT_DEFS[p] as { energyReward: number }).energyReward;
      }
    }
    return e;
  }

  /**
   * Combined build progress for `project` in colony `cid`, in permille (0..1000):
   * the average of the labour and food fractions. Complete reads 1000. For UI bars.
   */
  progressPermilleOf(cid: ColonyId, project: Project): number {
    const def = PROJECT_DEFS[project];
    if (def === undefined) return 0;
    if (this.isComplete(cid, project)) return 1000;
    const li = cid * PROJECT_COUNT + project;
    const lf = def.laborNeeded > 0 ? Math.min(1000, Math.floor((this.labor[li] as number) * 1000 / def.laborNeeded)) : 1000;
    const ff = def.foodCost > 0 ? Math.min(1000, Math.floor((this.food[li] as number) * 1000 / def.foodCost)) : 1000;
    return Math.floor((lf + ff) / 2);
  }

  /** Fold per-colony project state into a running FNV hash (determinism). */
  hash(): number {
    let h = fnvInit();
    for (let cid = 0; cid < this.colonies.count; cid++) {
      const base = cid * PROJECT_COUNT;
      for (let p = 0; p < PROJECT_COUNT; p++) {
        h = fnvU32(h, this.status[base + p] as number);
        h = fnvU32(h, this.labor[base + p] as number);
        h = fnvU32(h, this.food[base + p] as number);
      }
    }
    h = fnvU32(h, this.completedTotal);
    return h;
  }
}
